import type { FeatureFlag } from '@/state/useFeatureFlags';
import type { LayoutNode, PanelComponents } from '@/components/Workspace/types';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { GitBranch, GitCommitVertical } from 'lucide-react';
import { cn } from 'helpers/cn';
import { useFeatureFlags } from '@/state/useFeatureFlags';
import { WorkspaceLayout } from '@/components/Workspace/WorkspaceLayout';
import { EmptyState } from '../../../Dashboard/ConsolePanel';
import { CreateBranchModal } from './CreateBranchModal';

const CHIP = 'micro-label rounded-pill inline-flex w-fit border px-2 py-0.5';
const SECONDARY_BUTTON = 'border-line text-dim hover:bg-hover hover:text-foreground h-8 rounded-lg bg-transparent';

const LAYOUT: LayoutNode = {
  type: 'group',
  id: 'root',
  direction: 'horizontal',
  children: [
    {
      node: {
        type: 'group',
        id: 'left',
        direction: 'vertical',
        children: [
          { node: { type: 'panel', id: 'settings', appType: null, fitContent: true }, size: 50 },
          { node: { type: 'panel', id: 'actions', appType: null }, size: 50 },
        ],
      },
      size: 30,
    },
    { node: { type: 'panel', id: 'secondary', appType: null }, size: 70 },
  ],
};

type FeatureFlagSettingsProps = {
  flag: FeatureFlag;
};

type SettingsPanelProps = {
  flag: FeatureFlag;
  onCreateBranch: () => void;
  onUpdate: (payload: Partial<FeatureFlag>) => Promise<void>;
};

const SettingsPanel = ({ flag, onCreateBranch, onUpdate }: SettingsPanelProps) => {
  const [editingName, setEditingName] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [name, setName] = useState(flag.name);
  const [description, setDescription] = useState(flag.description);
  const nameRef = useRef<HTMLInputElement | null>(null);
  const descRef = useRef<HTMLTextAreaElement | null>(null);

  const saveName = async () => {
    setEditingName(false);
    const trimmed = name.trim();
    if (trimmed && trimmed !== flag.name) {
      await onUpdate({ name: trimmed });
    } else {
      setName(flag.name);
    }
  };

  const saveDescription = async () => {
    setEditingDesc(false);
    if (description !== flag.description) {
      await onUpdate({ description });
    }
  };

  return (
    <div className="flex h-full flex-col gap-3.5 overflow-auto p-3.5">
      <div className="flex flex-wrap items-center gap-2">
        {editingName ? (
          <Input
            ref={nameRef}
            value={name}
            onChange={(ev) => setName(ev.target.value)}
            onBlur={saveName}
            onKeyDown={(ev) => {
              if (ev.key === 'Enter') nameRef.current?.blur();
              if (ev.key === 'Escape') {
                setName(flag.name);
                setEditingName(false);
              }
            }}
            className="border-line bg-field text-foreground h-8 w-auto rounded-lg px-2 text-title font-medium"
            autoFocus
          />
        ) : (
          <h1
            className="text-foreground hover:text-dim m-0 cursor-pointer text-title font-medium transition-colors duration-150"
            onClick={() => setEditingName(true)}
          >
            {flag.name}
          </h1>
        )}
        <span className={cn(CHIP, 'border-line text-dim')}>{flag.scope}</span>
        <span className={cn(CHIP, 'border-sel-line bg-sel-bg text-sel')}>Development</span>
      </div>

      {editingDesc ? (
        <Textarea
          ref={descRef}
          value={description}
          onChange={(ev) => setDescription(ev.target.value)}
          onBlur={saveDescription}
          onKeyDown={(ev) => {
            if (ev.key === 'Escape') {
              setDescription(flag.description);
              setEditingDesc(false);
            }
          }}
          className="border-line bg-field text-foreground resize-none rounded-lg text-body"
          autoFocus
        />
      ) : (
        <p
          className="supporting hover:text-foreground m-0 cursor-pointer transition-colors duration-150"
          onClick={() => setEditingDesc(true)}
        >
          {flag.description || 'Add a description...'}
        </p>
      )}

      {flag.branch ? (
        <div className="text-accent flex items-center gap-2">
          <GitBranch className="h-3.5 w-3.5" />
          <code className="num">{flag.branch}</code>
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={onCreateBranch} className={cn(SECONDARY_BUTTON, 'self-start')}>
          <GitBranch className="mr-1.5 h-3.5 w-3.5" /> Create Feature Branch
        </Button>
      )}
    </div>
  );
};

type ActionsPanelProps = {
  flagBranch?: string;
  currentBranch: string | null;
  onCheckout: (branch: string) => void;
};

const ActionsPanel = ({ flagBranch, currentBranch, onCheckout }: ActionsPanelProps) => {
  const isOnFlagBranch = flagBranch && currentBranch === flagBranch;

  return (
    <div className="flex flex-col gap-3 p-3.5">
      <div className="flex flex-wrap items-center gap-2">
        <GitBranch className="text-dim h-3.5 w-3.5" />
        <span className="micro-label">current branch</span>
        {/* Being on the flag's own branch is the healthy state, so it reads as ok. */}
        <code className={cn('num', isOnFlagBranch ? 'text-ok' : 'text-foreground')}>{currentBranch ?? '...'}</code>
      </div>
      {flagBranch && !isOnFlagBranch && (
        <Button
          variant="outline"
          size="sm"
          className={cn(SECONDARY_BUTTON, 'self-start')}
          onClick={() => onCheckout(flagBranch)}
        >
          <GitBranch className="mr-1.5 h-3.5 w-3.5" /> Checkout {flagBranch}
        </Button>
      )}
      {isOnFlagBranch && (
        <Button
          variant="outline"
          size="sm"
          className={cn(SECONDARY_BUTTON, 'self-start')}
          onClick={() => onCheckout('master')}
        >
          <GitBranch className="mr-1.5 h-3.5 w-3.5" /> Checkout master
        </Button>
      )}
    </div>
  );
};

export const FeatureFlagSettings = ({ flag: initialFlag }: FeatureFlagSettingsProps) => {
  const { updateFlag, createBranch, getCurrentBranch, checkoutBranch } = useFeatureFlags();
  const [flag, setFlag] = useState(initialFlag);
  const [currentBranch, setCurrentBranch] = useState<string | null>(null);
  const [layout, setLayout] = useState<LayoutNode>(LAYOUT);
  const [branchModalOpen, setBranchModalOpen] = useState(false);

  useEffect(() => {
    getCurrentBranch().then((res) => setCurrentBranch(res.branch));
  }, []);

  const handleUpdate = async (payload: Partial<FeatureFlag>) => {
    await updateFlag(flag.id, payload);
    setFlag((prev) => ({ ...prev, ...payload }));
  };

  const handleCreateBranch = async (branch: string) => {
    const updated = await createBranch(flag.id, branch);
    setFlag((prev) => ({ ...prev, ...updated }));
    setBranchModalOpen(false);
  };

  const components: PanelComponents = {
    settings: () => (
      <SettingsPanel flag={flag} onCreateBranch={() => setBranchModalOpen(true)} onUpdate={handleUpdate} />
    ),
    actions: () => (
      <ActionsPanel
        flagBranch={flag.branch}
        currentBranch={currentBranch}
        onCheckout={async (branch) => {
          await checkoutBranch(branch);
          setCurrentBranch(branch);
        }}
      />
    ),
    secondary: () =>
      flag.branch ? (
        <div className="flex flex-col gap-3 p-3.5">
          <div className="text-accent flex items-center gap-2">
            <GitBranch className="h-4 w-4" />
            <code className="font-mono text-body">{flag.branch}</code>
          </div>
          <div className="flex items-center gap-2">
            <GitCommitVertical className="text-dim h-3.5 w-3.5" />
            <span className="micro-label">0 commits</span>
          </div>
        </div>
      ) : (
        <div className="flex h-full items-center justify-center p-8">
          <div className="max-w-sm text-center">
            <EmptyState>No branch created yet. Start by creating a branch for this new feature.</EmptyState>
          </div>
        </div>
      ),
  };

  return (
    <div className="h-full min-h-[420px]">
      <WorkspaceLayout layout={layout} onLayoutChange={setLayout} components={components} noHeader />
      <CreateBranchModal
        open={branchModalOpen}
        scope={flag.scope}
        flagName={flag.name}
        onConfirm={handleCreateBranch}
        onCancel={() => setBranchModalOpen(false)}
      />
    </div>
  );
};
